import { InputMoney, machineFormat, InputBasico, InputData, SelectBasico } from '@/components/Inputs'
import { Activity } from '@/components/Workflow/ActivityView/Activities/Activity'
import { IAskActivity, IProposta, IPropostaConfirmacaoValoresCreditoImobiliario } from '@/interfaces'
import { PropostaConfirmacaoValoresCreditoImobiliario } from '@/models'
import { AttaService, AttaServices, WorkflowService } from '@/services'
import { PROPOSTA_CONFIRMACAO_VALORES_ACAO, PROPOSTA_STATUS } from '@/types'
import { AxiosError } from 'axios'
import { DateTime } from 'luxon'
import { PropType } from 'vue'
import { Component, Prop, Model } from 'vue-property-decorator'
import { VCol, VForm, VRow } from 'vuetify/lib'
import { Constants } from '@/services'
import { FORNECEDORES } from '@/constants/Fornecedores'
import { RULES } from '@/constants'

@Component({})
export class ValuesConfirmationForm extends Activity {
	@Model('input', { type: Object as PropType<IAskActivity> }) declare readonly input: IAskActivity
	@Prop({ type: Object as PropType<IProposta> }) object!: IProposta
	@Prop({ type: Boolean }) routine!: boolean
	@Prop({ type: Boolean, required: false, default: true }) proposalExists!: boolean

	isValid: boolean = false
	isBlockedDate: boolean = false
	values: IPropostaConfirmacaoValoresCreditoImobiliario = {} as unknown as IPropostaConfirmacaoValoresCreditoImobiliario
	productOperationType: string = ''
	proposalData!: IProposta

	async beforeMount() {
		this.proposalData = this.object
		if (this.proposalExists) {
			await this.getProposta()
		}
	}

	async mounted() {
		if (this.proposalData && this.routine) {
			if (this.proposalExists) {
				this.blockedDate()
			}

			let valuesCopy = JSON.parse(JSON.stringify(this.proposalData.confirmacaoValores))

			valuesCopy = Object.keys(valuesCopy).reduce((acc: any, key: any) => {
				acc[key.charAt(0).toUpperCase() + key.slice(1)] = valuesCopy[key]
				return acc
			}, {})
			this.values = valuesCopy
		}

		if (!this.values.PropostaBancoId) {
			this.values.PropostaBancoId = this.proposalData.idPropostaPrd
		}

		if (this.proposalData.confirmacaoValores?.tipoOperacaoProduto && this.proposalData.confirmacaoValores.tipoOperacaoProduto > 0)
			this.productOperationType = this.getProductTypeOperationName(this.proposalData.confirmacaoValores.tipoOperacaoProduto)
	}

	get valorPagoVendedores() {
		let valor
		if (!this.values.ValorIQ) {
			valor = this.values.ValorFinanciamento + this.values.ValorEntradaFGTS
		}

		if (this.values.ValorIQ) {
			valor = this.values.ValorFinanciamento + this.values.ValorEntradaFGTS - this.values.ValorIQ
		}

		return Number(valor)
	}

	get valorTotalFinanciamento() {
		let valor = 0
		if (this.values.ValorFinanciamento) valor += this.values.ValorFinanciamento
		if (this.values.ValorTarifaAvaliacaBanco) valor += this.values.ValorTarifaAvaliacaBanco
		if (this.values.ValorITBI) valor += this.values.ValorITBI
		if (valor > 0) return Number(valor)
		return null
	}

	get valorEntrada() {
		let valor = this.values.ValorCompraVenda - this.values.ValorFinanciamento - this.values.ValorEntradaFGTS
		return valor
	}

	get user() {
		const { state } = this.$store
		return state.user?.name
	}

	public render() {
		if (!this.values) return ''

		return (
			<VForm
				ref="formConfirmation"
				lazy-validation
				vModel={this.isValid}
				vOn:submit_prevent={() => undefined}
				vOn:input={() => this.formEmitValuesChanged()}
			>
				<VRow>
					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							data-cy="valor-compra-venda"
							label="Valor compra e venda imóvel"
							placeholder="0,00"
							persistent-placeholder
							vModel={this.values.ValorCompraVenda}
							rules={[
								(v: string) => !!v || 'Campo obrigatório',
								(v: string) => machineFormat(v) >= 60000 || 'Compra e venda deve ser maior que 60 mil'
							]}
						/>
					</VCol>
					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-financiamento"
							label="Valor de financiamento"
							vModel={this.values.ValorFinanciamento}
							rules={[
								(v: string) => !!v || 'Campo obrigatório',
								() =>
									this.values.ValorFinanciamento <= this.values.ValorCompraVenda ||
									'O valor de financiamento deve ser menor que o valor de compra'
							]}
						/>
					</VCol>

					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-entrada-fgts"
							label="Valor entrada FGTS"
							vModel={this.values.ValorEntradaFGTS}
							rules={[
								(v: string) => !!v || this.proposalData.status == PROPOSTA_STATUS.CARTA_CREDITO || 'Campo obrigatório',
								() =>
									this.values.ValorEntradaFGTS <= this.values.ValorCompraVenda ||
									'Entrada do FGTS deve ser menor que o valor de compra e venda'
							]}
						/>
					</VCol>

					<VCol cols="4">
						<input type="hidden" vModel={this.valorEntrada} />
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							readonly={true}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-entrada-recursos-proprios"
							label="Valor entrada recurso próprio"
							value={this.valorEntrada}
							rules={[
								(v: string) =>
									machineFormat(v) <= this.values.ValorCompraVenda || 'Entrada do recurso próprio deve ser menor que o valor de compra'
							]}
						/>
					</VCol>

					<VCol cols="4">
						<SelectBasico
							disabled={this.isAnswered || this.isBlockedDate}
							readonly={this.productsByBank.length <= 1}
							items={this.productsByBank}
							vModel={this.productOperationType}
							label="Amortização"
							data-cy="tipo-amortização-credito-imobiliario"
							on={{
								change: (e: string) => this.handleOnChangeOperationProduct(e)
							}}
							outlined
							dense
							rules={[(v: string) => !!v || this.proposalData.status != PROPOSTA_STATUS.CARTA_CREDITO || 'Campo obrigatório']}
						/>
					</VCol>

					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							data-cy="valor-tarifa-avaliacao-banco"
							label="Valor avaliação banco"
							placeholder="0,00"
							persistent-placeholder
							vModel={this.values.ValorAvaliacaoBanco}
							rules={[(v: string) => !!v || this.proposalData.status == PROPOSTA_STATUS.CARTA_CREDITO || 'Campo obrigatório']}
						/>
					</VCol>

					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-itbi"
							label="Valor despesas adicionais"
							vModel={this.values.ValorITBI}
							rules={[(v: string) => !!v || this.proposalData.status == PROPOSTA_STATUS.CARTA_CREDITO || 'Campo obrigatório']}
						/>
					</VCol>

					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-avaliacao-banco"
							label="Tarifa avaliação banco"
							vModel={this.values.ValorTarifaAvaliacaBanco}
							rules={[]}
						/>
					</VCol>

					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							readonly={true}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-total-financiamento"
							label="Valor total financiamento"
							value={this.valorTotalFinanciamento}
							rules={[]}
						/>
					</VCol>

					<VCol cols="4">
						<InputBasico
							disabled={this.isAnswered || this.isBlockedDate}
							type="number"
							placeholder="000"
							persistent-placeholder
							suffix="meses"
							hide-spin-buttons
							data-cy="prazo-acordado"
							label="Prazo Acordado"
							vModel={this.values.Prazo}
							rules={[(v: number) => !!v || 'Campo Obrigatório', (v: number) => !v || v <= 420 || 'Prazo máximo 420 meses']}
						/>
					</VCol>

					<VCol cols="4">
						<InputBasico
							disabled={this.isAnswered || this.isBlockedDate}
							type="number"
							prefix="%"
							placeholder="0"
							persistent-placeholder
							hide-spin-buttons
							data-cy="taxa-acordada"
							label="Taxa Acordada"
							vModel={this.values.ValorTaxaAcordada}
							rules={[(v: number) => !v || v <= 100 || 'Taxa deve ser menor que 100', (v: number) => !!v || 'Campo Obrigatório']}
						/>
					</VCol>

					<VCol cols="4">
						<InputMoney
							disabled={this.isAnswered || this.isBlockedDate}
							label="Valor de IQ"
							vModel={this.values.ValorIQ}
							vOn:input={() => {
								if (!this.values.ValorIQ) this.values.DataIQ = null
							}}
						/>
					</VCol>

					<VCol cols="4">
						<InputData disabled={this.isAnswered || !this.values.ValorIQ} label="Data IQ" vModel={this.values.DataIQ} />
					</VCol>

					<VCol cols="4">
						<InputMoney
							readonly
							disabled={this.isAnswered || this.isBlockedDate}
							placeholder="0,00"
							persistent-placeholder
							data-cy="valor-pgto-vendedores"
							label="Valor Pgto Vendedores"
							value={this.valorPagoVendedores}
						/>
					</VCol>
				</VRow>
			</VForm>
		)
	}

	async getProposta() {
		const id = parseInt(this.$route.params.propostaId)
		try {
			let { result } = await AttaServices.getProposta(id)
			this.proposalData = result
		} catch (err: any | AxiosError) {
			console.log(err)
		}
	}

	/**
	 * Blocked Date
	 * @description checks if the date is blocked
	 */
	blockedDate() {
		const { state } = this.$store
		if (state?.user?.rules?.includes(RULES.ISOLVE_CLOSING_ALLOW_ADM_ROUTINES)) {
			return
		}

		if (this.proposalData.dtFinalizacao) {
			let blockedDate = DateTime.fromISO(this.proposalData.dtFinalizacao).plus({ months: 1 }).set({ day: 20 }).toISODate()
			let today = DateTime.local().toISODate()
			if (today > blockedDate && this.proposalData.idFornecEmissao != FORNECEDORES.CAIXA) {
				this.$emit('routine:blockedDate')
				this.isBlockedDate = true
			}
		}
	}

	/**
	 * @description Verifica se já existem dados preenchidos nesse formulário.
	 * Se os valores já foram confirmados, carrega o objeto confirmacaoValores
	 * Se os valores foram salvos, carrega do metadata answer
	 */
	initFormData() {
		let inputs = JSON.parse(this.input.metadata.answer)
		let confirmacao = this.proposalData.confirmacaoValores as unknown as { [key: string]: any }

		if (inputs.PropostaId != 0) {
			this.values = new PropostaConfirmacaoValoresCreditoImobiliario(this.input.metadata.answer)
			return
		}

		if (inputs.PropostaId === 0 && confirmacao) {
			const newConfirmacao = Object.fromEntries(
				Object.entries(confirmacao).map(([key, value]) => [key[0].toUpperCase() + key.substring(1), value])
			)
			//@ts-ignore
			this.values = newConfirmacao
		}
		if (inputs.TipoOperacaoProduto) {
			this.values.TipoOperacaoProduto = inputs.TipoOperacaoProduto
			this.productOperationType = this.getProductTypeOperationName(inputs.TipoOperacaoProduto)
		}
	}

	/**
	 * @params acao {number}
	 * @description Atribui a ação do formulário e atribui também algumas propriedades do formulário para o objeto
	 * Em caso de salvar informações: ele bate na API e salva no metadata answer
	 * Em caso de confirmar valores: ele bate na API salva, e depois novamente, confirmando os valores
	 */
	async submit(action: PROPOSTA_CONFIRMACAO_VALORES_ACAO) {
		this.values.Acao = action
		this.values.PropostaId = this.input.propostaId
		this.values.PropostaBancoId = this.proposalData.idPropostaPrd
		this.values.UserId = Number(this.$store.state.user.id)

		this.values.ValorEntrada = this.valorEntrada
		this.values.ValorPagamentoVendedores = this.valorPagoVendedores
		this.values.TipoOperacaoProduto = this.getProductTypeOperationId(this.productOperationType) || -1

		const requestBody = { ...this.values }
		requestBody.DataIQ = this.values.DataIQ ? DateTime.fromFormat(this.values.DataIQ, 'dd/MM/yyyy').toJSON() : null

		const form = this.$refs['formConfirmation'] as FormElement
		const isFormValid = form.validate()

		if (isFormValid) {
			try {
				const response = await WorkflowService.answerActivity(this.input.id, requestBody)
				if (response) this.$emit('sent:success', action)
			} catch (err: any | AxiosError) {
				this.$emit('sent:error', err.message)
			}
		}

		if (!isFormValid) this.setLoading(false)

		return isFormValid
	}

	/**
	 * Update Values Confirmation
	 * @description Update values confirmation in api value confirmation
	 * @returns
	 */
	async updateValuesConfirmation() {
		const valuesCopy = Object.fromEntries(
			Object.entries(this.values).map(([key, value]) => [key[0].toLowerCase() + key.substring(1), value])
		)
		if (valuesCopy.dataIQ) {
			valuesCopy.PossuiIQ = true
			valuesCopy.dataIQ = DateTime.fromFormat(this.values.DataIQ, 'dd/MM/yyyy').toJSON()
		} else {
			valuesCopy.PossuiIQ = false
			valuesCopy.dataIQ = null
		}

		if (valuesCopy.acao) valuesCopy.acao = PROPOSTA_CONFIRMACAO_VALORES_ACAO.CONFIRMAR

		try {
			const response = await AttaService.postValuesConfirmation(this.proposalData.id, valuesCopy)
			if (response) {
				this.addObservation()
				this.$emit('routine:updateForm')
				return true
			}
		} catch (err: any | AxiosError) {
			return false
		}
	}

	async addObservation() {
		const { propostaId } = this.$route.params
		let observacao = `  Confirmação de valores alterada por ${this.$store.state.user.name}`
		try {
			await AttaServices.addPropostaObservacao(Number(propostaId), observacao)
		} catch (err: any | AxiosError) {
			console.log(err)
		}
	}

	get productsByBank() {
		let products = new Array<string>()
		if (this.proposalData) {
			if (this.proposalData.idFornecEmissao == FORNECEDORES.CAIXA) {
				let filtered = Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.CAIXA.filter((p) => p.bancoOpera)
				if (filtered.length > 0) products = filtered.map((p) => p.nome)
			}

			if (this.proposalData.idFornecEmissao == FORNECEDORES.BRADESCO) {
				let filtered = Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.BRADESCO.filter((p) => p.bancoOpera)
				if (filtered.length > 0) products = filtered.map((p) => p.nome)
			}

			if (this.proposalData.idFornecEmissao == FORNECEDORES.ITAU) {
				let filtered = Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.ITAU.filter((p) => p.bancoOpera)
				if (filtered.length > 0) products = filtered.map((p) => p.nome)
			}

			if (this.proposalData.idFornecEmissao == FORNECEDORES.SANTANDER) {
				let filtered = Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.SANTANDER.filter((p) => p.bancoOpera)
				if (filtered.length > 0) products = filtered.map((p) => p.nome)
			}
		}

		if (products.length == 0) {
			products = Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.ALL.map((p) => p.nome)
		}

		return products
	}

	getProductTypeOperationName(id: number) {
		return Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.ALL.find((p) => p.id == id)?.nome || ''
	}

	getProductTypeOperationId(name: string) {
		return Constants.PRODUTOS_BANCO_CREDITO_IMOBILIARIO.ALL.find((p) => p.nome == name)?.id
	}

	handleOnChangeOperationProduct(item: string) {
		this.values.TipoOperacaoProduto = this.getProductTypeOperationId(item) || -1
	}

	formEmitValuesChanged() {
		if (this.routine) {
			this.values.ValorEntrada = this.valorEntrada
			this.values.ValorPagamentoVendedores = this.valorPagoVendedores
			this.values.TipoOperacaoProduto = this.getProductTypeOperationId(this.productOperationType) || -1

			this.$emit('routine:updatedConfValValues', this.values)
		}
	}
}

export default ValuesConfirmationForm
